if (!customElements.get('gm-purchase-bar')) {
  customElements.define('gm-purchase-bar', class GMPurchaseBar extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.frame = 0;
      this.wasBlocked = false;
      this.form = document.getElementById(this.dataset.formId);
      this.info = document.getElementById(`ProductInfo-${this.dataset.sectionId}`);
      this.mainButton = this.form?.querySelector('[name="add"]');
      if (!this.form || !this.info || !this.mainButton || !window.IntersectionObserver) return;

      try {
        this.variants = JSON.parse(this.querySelector('[data-purchase-variants]').textContent);
      } catch {
        return;
      }

      this.picker = this.info.querySelector('variant-selects, variant-radios');
      this.addButton = this.querySelector('[data-purchase-add]');
      this.editButton = this.querySelector('[data-purchase-edit]');
      this.label = this.querySelector('[data-purchase-label]');
      this.price = this.querySelector('[data-purchase-price]');
      this.selection = this.querySelector('[data-purchase-selection]');
      this.error = this.querySelector('[data-purchase-error]');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      this.spacer = document.createElement('div');
      this.spacer.className = 'gm-purchase-bar-spacer';
      this.spacer.setAttribute('aria-hidden', 'true');
      document.body.appendChild(this.spacer);

      this.refresh = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          if (!this.isConnected) return;
          this.syncState();
          this.syncVisibility();
        });
      };
      const listener = { signal: this.abort.signal };
      this.info.addEventListener('change', this.refresh, listener);
      this.info.addEventListener('input', this.refresh, listener);
      this.mobile.addEventListener('change', this.refresh, listener);
      document.addEventListener('toggle', this.refresh, { ...listener, capture: true });
      document.addEventListener('focusin', this.refresh, listener);
      document.addEventListener('focusout', this.refresh, listener);
      window.addEventListener('resize', this.refresh, { ...listener, passive: true });
      window.addEventListener('pageshow', this.refresh, listener);

      this.addButton.addEventListener('click', () => {
        this.syncState();
        if (this.addButton.disabled) return;
        this.restoreAfterDrawer = true;
        this.mainButton.click();
        this.refresh();
      }, listener);
      this.editButton.addEventListener('click', () => this.editSelection(), listener);

      this.formObserver = new MutationObserver(this.refresh);
      this.formObserver.observe(this.info, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden', 'value']
      });
      this.overlayObserver = new MutationObserver(this.refresh);
      this.overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      document.querySelectorAll('cart-drawer, cart-notification, .cart-notification, modal-dialog, quick-add-modal, dialog, header-drawer, .sign-up-popup-modal').forEach((overlay) => {
        this.overlayObserver.observe(overlay, { attributes: true, attributeFilter: ['class', 'open', 'aria-hidden'] });
      });
      this.intersection = new IntersectionObserver(this.refresh, { threshold: [0, 1] });
      this.intersection.observe(this.mainButton);
      if (window.ResizeObserver) {
        this.resize = new ResizeObserver(() => {
          if (this.hidden) return;
          this.spacer.style.setProperty('--gm-purchase-bar-height', `${Math.ceil(this.getBoundingClientRect().height)}px`);
        });
        this.resize.observe(this);
      }
      if (this.mainButton.hasAttribute('aria-haspopup')) this.addButton.setAttribute('aria-haspopup', this.mainButton.getAttribute('aria-haspopup'));
      this.refresh();
    }

    syncState() {
      const id = this.form.querySelector('[name="id"]')?.value;
      const invalidSelection = this.picker && 'currentVariant' in this.picker && !this.picker.currentVariant;
      const variant = invalidSelection ? null : this.variants[id];
      const busy = this.mainButton.classList.contains('loading');
      const unavailable = !variant || variant.available === false || this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
      this.addButton.disabled = unavailable || busy;
      this.addButton.setAttribute('aria-busy', String(busy));
      const text = busy ? this.dataset.adding : this.mainButton.querySelector('span:not(.hidden)')?.textContent.trim() || this.dataset.unavailable;
      if (this.label.textContent !== text) this.label.textContent = text;
      this.price.hidden = !variant;
      const unpricedUnavailable = variant?.priceAmount === 0 && variant.available === false;
      const priceText = unpricedUnavailable ? this.dataset.currentlyUnavailable : variant?.price;
      this.price.classList.toggle('gm-purchase-bar__price--unavailable', unpricedUnavailable);
      if (variant && this.price.textContent !== priceText) this.price.textContent = priceText;
      const quantity = Array.from(document.querySelectorAll('input[name="quantity"]')).find((input) => input.form === this.form)?.value;
      let selected = variant?.title || (variant ? this.dataset.defaultSelection : this.dataset.unavailable);
      if (quantity && Number(quantity) > 1) selected += ` × ${quantity}`;
      if (this.selection.textContent !== selected) this.selection.textContent = selected;
      this.selection.title = selected;
      const mainError = this.info.querySelector('.product-form__error-message-wrapper:not([hidden]) .product-form__error-message');
      const error = mainError?.textContent.trim() || '';
      this.error.hidden = !error;
      if (this.error.textContent !== error) this.error.textContent = error;
      if (error && !busy) this.restoreAfterDrawer = false;
    }

    syncVisibility() {
      const blocked = document.body.classList.contains('overflow-hidden') || document.body.classList.contains('overflow-hidden-mobile') || document.querySelector('cart-drawer.active, cart-notification.active, .cart-notification.active, modal-dialog[open], quick-add-modal[open], dialog[open], header-drawer details[open], .sign-up-popup-modal.popup-modal--active');
      const typing = document.activeElement?.matches('textarea, input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"])');
      const visible = this.mobile.matches && !blocked && !typing && this.mainButton.getClientRects().length > 0 && this.mainButton.getBoundingClientRect().bottom <= 0;
      if (!visible && !blocked && this.contains(document.activeElement)) this.mainButton.focus({ preventScroll: true });
      this.hidden = !visible;
      if (this.wasBlocked && !blocked && visible && this.restoreAfterDrawer) {
        this.addButton.focus({ preventScroll: true });
        this.restoreAfterDrawer = false;
      }
      this.wasBlocked = Boolean(blocked);
    }

    editSelection() {
      const target = this.picker?.querySelector('select, input[type="radio"]:checked, input[type="radio"]') || this.info.querySelector('input[name="quantity"]') || this.mainButton;
      target.scrollIntoView({ behavior: this.motion.matches ? 'instant' : 'smooth', block: 'center' });
      target.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.formObserver?.disconnect();
      this.overlayObserver?.disconnect();
      this.intersection?.disconnect();
      this.resize?.disconnect();
      this.spacer?.remove();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  });
}
